import React, { useMemo, useEffect, useState } from 'react';
import TaskItem from './TaskItem';
import QuickAddTask from './QuickAddTask';

const TaskListView = ({ tasks, onTaskClick, sort, onSortChange, onTaskCreated, onToggleComplete, onAddSubTask, onSelectBusinessUnit, deriveBusinessUnitId, businessUnitMap = {} }) => {
    const [isMobile, setIsMobile] = useState(false);
    const sortedTasks = useMemo(() => {
        if (!tasks) return [];
        const sorted = [...tasks];
        sorted.sort((a, b) => {
            let valA = a[sort.field];
            let valB = b[sort.field];

            // Handle nested properties or specific fields
            if (sort.field === 'status') {
                valA = a.properties?.status || 'waiting';
                valB = b.properties?.status || 'waiting';
            }

            if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [tasks, sort]);

    const handleSort = (field) => {
        if (sort.field === field) {
            onSortChange({ field, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
        } else {
            onSortChange({ field, direction: 'asc' });
        }
    };

    useEffect(() => {
        const compute = () => setIsMobile(window.innerWidth <= 768);
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    const renderSortIcon = (field) => {
        if (sort.field !== field) return <i className="fa fa-sort text-muted"></i>;
        return sort.direction === 'asc' ? <i className="fa fa-sort-asc"></i> : <i className="fa fa-sort-desc"></i>;
    };

    const hasTasks = sortedTasks.length > 0;

    const getUnitMeta = (task) => {
        const derivedUnitId = deriveBusinessUnitId ? deriveBusinessUnitId(task) : '';
        const fallbackUnitId = task.unit_id || task.unitId || '';
        const unitId = derivedUnitId ? String(derivedUnitId) : (fallbackUnitId ? String(fallbackUnitId) : '');
        const unit = unitId && businessUnitMap ? businessUnitMap[unitId] : null;
        return {
            unitId,
            unitName: (unit && (unit.title || unit.name)) || '',
            unitColor: task.unit_color || (unit && (unit.color || unit.colour || unit.display_color || unit.displayColour)),
        };
    };

    const renderMobileStatus = (task) => {
        const statusValue = (task.properties?.status || '').toLowerCase();
        const isCompleted = task.properties?.completed === 'yes';
        const isRejected = statusValue === 'rejected';
        const isInReview = statusValue === 'in review' || statusValue === 'in_review';

        const statusLabel = isCompleted
            ? 'Completed'
            : isRejected
                ? 'Rejected'
                : isInReview
                    ? 'In Review'
                    : 'In Progress';

        const statusClass = isCompleted
            ? 'is-complete'
            : isRejected
                ? 'is-rejected'
                : isInReview
                    ? 'is-review'
                    : 'is-progress';

        return { statusLabel, statusClass };
    };

    const renderMobileCards = (items, level = 0) => {
        if (!Array.isArray(items) || items.length === 0) return null;
        return items.map((task) => {
            const children = task.sub_tasks || task.subTasks || [];
            const dueDate = task.date_due ? new Date(task.date_due).toLocaleDateString() : '-';
            const { unitId, unitName, unitColor } = getUnitMeta(task);
            const { statusLabel, statusClass } = renderMobileStatus(task);
            const description = task.description ? task.description.replace(/<[^>]*>?/gm, '') : '';

            return (
                <div key={task.id} className="task-mobile-card" style={{ marginLeft: level > 0 ? `${level * 10}px` : 0 }}>
                    {unitColor && (
                        <button
                            type="button"
                            className="task-unit-bar"
                            style={{ background: unitColor, left: 4 }}
                            title={unitName || 'Business Unit'}
                            aria-label={`Filter by ${unitName || 'Business Unit'}`}
                            onClick={(e) => { e.stopPropagation(); if (unitId && onSelectBusinessUnit) onSelectBusinessUnit(unitId); }}
                        />
                    )}
                    <div className="task-mobile-main" onClick={() => onTaskClick(task.id)}>
                        <div className="task-mobile-title-row">
                            <div className="task-mobile-title">{task.title}</div>
                            <span className={`task-status-pill ${statusClass}`}>{statusLabel}</span>
                        </div>
                        <div className="task-mobile-meta">
                            <span className="task-mobile-meta-item"><i className="fa fa-calendar"></i> {dueDate}</span>
                        </div>
                        {description && <div className="task-mobile-desc">{description}</div>}
                    </div>
                    <div className="task-mobile-actions">
                        <button className="task-view-btn" onClick={(e) => { e.stopPropagation(); onTaskClick(task.id); }} title="Open details">
                            <i className="fa fa-eye"></i>
                        </button>
                        <button className="task-add-btn" onClick={(e) => { e.stopPropagation(); onAddSubTask(task.id); }} title="Add Sub-task">
                            <i className="fa fa-plus"></i>
                        </button>
                    </div>
                    {children.length > 0 && (
                        <div className="task-mobile-children">
                            {renderMobileCards(children, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    if (isMobile) {
        return (
            <div className="task-mobile-list">
                {hasTasks ? (
                    renderMobileCards(sortedTasks)
                ) : (
                    <div className="task-mobile-empty">No tasks match the current filters.</div>
                )}
            </div>
        );
    }

    return (
        <div className="task-table-container">
            <table className="task-table">
                <thead>
                    <tr>
                        <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                            Name {renderSortIcon('title')}
                        </th>
                        <th>Description</th>
                        <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                            Created {renderSortIcon('created_at')}
                        </th>
                        <th onClick={() => handleSort('date_due')} style={{ cursor: 'pointer' }}>
                            Due {renderSortIcon('date_due')}
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                            Status {renderSortIcon('status')}
                        </th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <QuickAddTask onTaskCreated={onTaskCreated} variant="table" />
                    {hasTasks ? (
                        sortedTasks.map(task => (
                            <TaskItem
                                key={task.id}
                                task={task}
                                onTaskClick={onTaskClick}
                                level={0}
                                onToggleComplete={onToggleComplete}
                                onAddSubTask={onAddSubTask}
                                onSelectBusinessUnit={onSelectBusinessUnit}
                                deriveBusinessUnitId={deriveBusinessUnitId}
                                businessUnitMap={businessUnitMap}
                            />
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                                No tasks match the current filters.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default TaskListView;
